import { addDays, getHours, getMinutes, setHours, setMinutes } from 'date-fns';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useCreateCalendarBlock } from '@/api/partners/reservations/use-create-calendar-block';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Input, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Icon } from '@/ui/icon';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';

interface BlockCarModalProps {
  visible?: boolean;
  startDate: Date;
  endDate: Date;
  car: Car;
  onClose: () => void;
  onSuccess: () => void;
  style?: any;
}

export function BlockCarModal({
  visible = false,
  startDate,
  endDate,
  car,
  onClose,
  onSuccess,
  style,
}: BlockCarModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [showStartTimeCalendar, setShowStartTimeCalendar] =
    React.useState(false);
  const [showEndTimeCalendar, setShowEndTimeCalendar] = React.useState(false);
  const [startDatetime, setStartDatetime] = useState<Date>(new Date(startDate));
  const [endDatetime, setEndDatetime] = useState<Date>(new Date(endDate));
  const [errorMessage, setErrorMessage] = useState<string>('');
  const minDate = new Date();

  const carZoneShort = DateTime.now().setZone(
    car.defaultLocation.timezone
  ).offsetNameShort;

  const {
    mutateAsync: createCalendarBlock,
    isPending: isCreatingCalendarBlock,
  } = useCreateCalendarBlock();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  useEffect(() => {
    setStartDatetime(new Date(startDate));
    setEndDatetime(new Date(endDate));
  }, [startDate, endDate]);

  const handleCreateCalendarBlock = async () => {
    try {
      await createCalendarBlock({
        startDate: DateTime.fromJSDate(startDatetime)
          .setZone(car.defaultLocation.timezone, { keepLocalTime: true })
          .toISO(),
        endDate: DateTime.fromJSDate(endDatetime)
          .setZone(car.defaultLocation.timezone, { keepLocalTime: true })
          .toISO(),
        carId: car.id,
      });

      onSuccess();
    } catch (e: any) {
      setErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View
        style={{
          ...styles.container,
          ...style,
        }}
        onStartShouldSetResponder={() => true}
        onResponderRelease={() => {
          setShowStartTimeCalendar(false);
          setShowEndTimeCalendar(false);
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            paddingLeft: 16,
            paddingTop: 16,
          }}
        >
          <View style={{ gap: 8 }}>
            <Text type="title2">Block Car</Text>

            <Text type="bodyRegular">All times in {carZoneShort}</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <View style={{ padding: 16, gap: 24, zIndex: 2 }}>
          <View style={{ position: 'relative', zIndex: 1 }}>
            <Pressable
              onPress={() => {
                setShowEndTimeCalendar(false);
                setShowStartTimeCalendar(!showStartTimeCalendar);
              }}
            >
              <Input
                label="Start time"
                placeholder={
                  startDatetime
                    ? startDatetime.toLocaleString()
                    : 'Select date and time'
                }
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<CalendarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
                disabled
              />
            </Pressable>

            <Calendar
              isVisible={showStartTimeCalendar}
              minDate={minDate}
              defaultStartDate={startDatetime}
              onHide={() => {
                setShowStartTimeCalendar(false);
              }}
              onDateSelected={(startDt) => {
                const formattedStartDatetime = setMinutes(
                  setHours(startDt, getHours(startDatetime)),
                  getMinutes(startDatetime)
                );

                if (formattedStartDatetime < minDate) {
                  setStartDatetime(minDate);
                  return;
                }

                if (endDatetime && formattedStartDatetime >= endDatetime) {
                  setStartDatetime(formattedStartDatetime);

                  const newEndDatetime = addDays(formattedStartDatetime, 3);
                  setEndDatetime(newEndDatetime);

                  return;
                }

                setStartDatetime(formattedStartDatetime);
              }}
              onTimeSelected={(startDt) => {
                if (endDatetime && startDt >= endDatetime) {
                  const newEndDatetime = addDays(startDt, 3);
                  setEndDatetime(newEndDatetime);
                }

                setStartDatetime(startDt);

                setShowEndTimeCalendar(true);
              }}
              range={false}
              style={{
                zIndex: 1,
                position: 'absolute',
                bottom: 100,
                right: 550,
              }}
              containerStyle={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                shadowColor: theme.colors.card.default.borderColor,
                shadowOffset: {
                  width: 0,
                  height: 5,
                },
                shadowOpacity: 0.5,
                shadowRadius: 5,
                elevation: 10,
              }}
              id="start-time-calendar"
              hasBackdrop={false}
              coverScreen={false}
            />
          </View>

          <View style={{ position: 'relative', gap: 16 }}>
            <Pressable
              onPress={() => {
                setShowStartTimeCalendar(false);
                setShowEndTimeCalendar(!showEndTimeCalendar);
              }}
              style={{ zIndex: -1 }}
            >
              <Input
                label="End time"
                placeholder={
                  endDatetime
                    ? endDatetime.toLocaleString()
                    : 'Select date and time'
                }
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<CalendarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
                disabled
              />
            </Pressable>

            <Calendar
              isVisible={showEndTimeCalendar}
              minDate={minDate}
              defaultStartDate={endDatetime}
              onHide={() => {
                setShowEndTimeCalendar(false);
              }}
              onDateSelected={(endDt) => {
                setEndDatetime(
                  setMinutes(
                    setHours(endDt, getHours(endDatetime)),
                    getMinutes(endDatetime)
                  )
                );
              }}
              onTimeSelected={(endDt) => {
                console.log('endDt', endDt);
                setEndDatetime(endDt);
                // setShowEndTimeCalendar(false);
              }}
              range={false}
              style={{
                zIndex: 2,
                position: 'absolute',
                bottom: 150,
                right: 550,
              }}
              containerStyle={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                shadowColor: theme.colors.card.default.borderColor,
                shadowOffset: {
                  width: 0,
                  height: 5,
                },
                shadowOpacity: 0.5,
                shadowRadius: 5,
                elevation: 10,
              }}
              id="end-time-calendar"
              hasBackdrop={false}
              coverScreen={false}
            />
          </View>
        </View>

        <View
          style={{
            flexDirection: 'column',
            alignItems: 'flex-end',
            padding: 16,
            gap: 16,
          }}
        >
          <View style={styles.buttonsContainer}>
            <Button
              text="Create Block"
              type="primary"
              onPress={handleCreateCalendarBlock}
              style={styles.button}
            />

            <Button
              text="Cancel"
              type="ghost"
              onPress={() => onClose()}
              style={styles.button}
            />
          </View>

          {errorMessage ? (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Error: {errorMessage}
            </Text>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
    backgroundColor: theme.colors.dialog.backgroundColor,
    borderRadius: 16,
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  buttonsContainer: {
    width: {
      xs: '100%',
      md: 'auto',
    },
    gap: 8,
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    justifyContent: {
      xs: 'center',
      md: 'flex-end',
    },
  },
}));
